import clsx from "clsx";
import { round } from "lodash-es";
import { ReactNode } from "react";

import { MonitoringEventTypeType } from "../../shared/types/monitoring";

import { TooltipTrigger } from "mds/components/TooltipTrigger";
import {
  CircleCheckFilledIcon,
  CircleCrossFilledIcon,
  CircleExclamationFilledIcon,
} from "mds/icons";
import { selectMonitoringStats } from "store/selectors";
import { useObjectSelector } from "store/store-hooks";
import { getParticipantCountBySectionId, getStatsBySectionIdAndType } from "utils/monitoring";

type TDPropTypes = {
  conditionForDanger?: boolean;
  conditionForInfo?: boolean;
  conditionForWarning?: boolean;
  noWrap?: boolean;
  scrollX?: boolean;
  onClick?: () => void;
  title?: string;
  children: ReactNode;
  className?: string;
  dataTestId?: string;
};

export const TD = ({
  conditionForDanger,
  conditionForInfo,
  conditionForWarning,
  noWrap,
  scrollX,
  onClick,
  title,
  children,
  className,
  dataTestId,
}: TDPropTypes) => {
  const cName = clsx(
    {
      "scroll-x": scrollX,
      "tooltip-container": title,
    },
    className,
  );
  return (
    <td
      className={cName}
      data-testid={dataTestId}
      style={{
        whiteSpace: noWrap ? "nowrap" : "normal",
        padding: "0.3rem",
        backgroundColor: conditionForDanger
          ? "#f5c6cb"
          : conditionForWarning
            ? "#ffeeba"
            : conditionForInfo
              ? "#bee5eb"
              : undefined,
      }}
      title={title}
      onClick={onClick}
    >
      {children}
      {/* {title ? <span>{title}</span> : null} */}
    </td>
  );
};

export const TDStatusIconWithTooltip = ({
  conditionForDanger = false,
  conditionForWarning = false,
  children,
  dataTestId,
}: {
  conditionForDanger?: boolean;
  conditionForWarning?: boolean;
  children: ReactNode;
  dataTestId?: string;
}) => {
  if (!children) return <TD dataTestId={dataTestId}> </TD>;
  return (
    <TD
      className="center"
      conditionForDanger={conditionForDanger}
      conditionForWarning={conditionForWarning}
      dataTestId={dataTestId}
    >
      <TooltipTrigger
        content={children}
        label={
          conditionForDanger ? "Error status" : conditionForWarning ? "Warning status" : "OK status"
        }
      >
        {conditionForDanger ? (
          <CircleCrossFilledIcon className="text-red" />
        ) : conditionForWarning ? (
          <CircleExclamationFilledIcon className="text-orange" />
        ) : (
          <CircleCheckFilledIcon className="text-green" />
        )}
      </TooltipTrigger>
    </TD>
  );
};

export const PageLoadStatsTD = ({ sectionId }: { sectionId: string }) => {
  // Record keyed by sectionId; consumers look up arbitrary sections.
  // eslint-disable-next-line @minervaproject/no-object-from-use-app-selector/require-properties-on-selector-hooks
  const stats = useObjectSelector(selectMonitoringStats);
  const nParticipants = getParticipantCountBySectionId(stats, sectionId);
  const { mean } = getStatsBySectionIdAndType(
    stats,
    sectionId,
    MonitoringEventTypeType.ENTER_CLASS,
  );
  return mean ? (
    <TD
      conditionForDanger={mean > 2 && nParticipants > 2}
      conditionForWarning={mean > 1.2 && nParticipants > 2}
      dataTestId="page-load-stats"
    >
      {`${round(mean, 2)}`}
    </TD>
  ) : (
    <TD dataTestId="page-load-stats">-</TD>
  );
};

export const WebsocketDisconnectionsTD = ({ sectionId }: { sectionId: string }) => {
  // Record keyed by sectionId; consumers look up arbitrary sections.
  // eslint-disable-next-line @minervaproject/no-object-from-use-app-selector/require-properties-on-selector-hooks
  const stats = useObjectSelector(selectMonitoringStats);
  const { formattedMean } = getStatsBySectionIdAndType(
    stats,
    sectionId,
    MonitoringEventTypeType.WEBSOCKET_DISCONNECTED,
  );
  return <TD dataTestId="websocket-disconnections-stats">{formattedMean}</TD>;
};

export const FailedApiRequestsTD = ({ sectionId }: { sectionId: string }) => {
  // Record keyed by sectionId; consumers look up arbitrary sections.
  // eslint-disable-next-line @minervaproject/no-object-from-use-app-selector/require-properties-on-selector-hooks
  const stats = useObjectSelector(selectMonitoringStats);
  const { formattedMean } = getStatsBySectionIdAndType(
    stats,
    sectionId,
    MonitoringEventTypeType.FAILED_API_REQUEST,
  );
  return <TD dataTestId="failed-api-requests-stats">{formattedMean}</TD>;
};

export const PageWorksheetLoadErrorTD = ({ sectionId }: { sectionId: string }) => {
  // Record keyed by sectionId; consumers look up arbitrary sections.
  // eslint-disable-next-line @minervaproject/no-object-from-use-app-selector/require-properties-on-selector-hooks
  const stats = useObjectSelector(selectMonitoringStats);
  const { formattedMean } = getStatsBySectionIdAndType(
    stats,
    sectionId,
    MonitoringEventTypeType.PAGE_WORKSHEET_LOAD_ERROR,
  );
  return <TD dataTestId="page-worksheet-load-error-stats">{formattedMean}</TD>;
};

export const PoorConnectionWarningTD = ({ sectionId }: { sectionId: string }) => {
  // Record keyed by sectionId; consumers look up arbitrary sections.
  // eslint-disable-next-line @minervaproject/no-object-from-use-app-selector/require-properties-on-selector-hooks
  const stats = useObjectSelector(selectMonitoringStats);
  const { formattedMean } = getStatsBySectionIdAndType(
    stats,
    sectionId,
    MonitoringEventTypeType.POOR_CONNECTION_WARNING,
  );
  return <TD dataTestId="poor-connection-warning-stats">{formattedMean}</TD>;
};

export const PreclassCheckFailStatsTD = ({ sectionId }: { sectionId: string }) => {
  // Record keyed by sectionId; consumers look up arbitrary sections.
  // eslint-disable-next-line @minervaproject/no-object-from-use-app-selector/require-properties-on-selector-hooks
  const stats = useObjectSelector(selectMonitoringStats);
  const { formattedMean, mean, percentAffectedUsers, nAffectedUsers } = getStatsBySectionIdAndType(
    stats,
    sectionId,
    MonitoringEventTypeType.PRECLASS_CHECK_FAIL,
  );
  return (
    <TD
      conditionForDanger={
        (mean && mean >= 0.4) ||
        (!!percentAffectedUsers && percentAffectedUsers >= 30 && nAffectedUsers > 3)
      }
      conditionForWarning={
        (mean && mean >= 0.25) || (!!percentAffectedUsers && percentAffectedUsers >= 20)
      }
      dataTestId="preclass-check-fail-stats"
    >
      {formattedMean}
    </TD>
  );
};

export const AvRefreshStatsTD = ({ sectionId }: { sectionId: string }) => {
  // Record keyed by sectionId; consumers look up arbitrary sections.
  // eslint-disable-next-line @minervaproject/no-object-from-use-app-selector/require-properties-on-selector-hooks
  const stats = useObjectSelector(selectMonitoringStats);
  const { formattedMean, mean, percentAffectedUsers, nAffectedUsers } = getStatsBySectionIdAndType(
    stats,
    sectionId,
    MonitoringEventTypeType.AV_REFRESH,
  );
  return (
    <TD
      conditionForDanger={
        (mean && mean >= 0.6) ||
        (!!percentAffectedUsers && percentAffectedUsers >= 30 && nAffectedUsers > 3)
      }
      conditionForWarning={
        (mean && mean >= 0.3) || (!!percentAffectedUsers && percentAffectedUsers >= 20)
      }
      dataTestId="av-refresh-stats"
    >
      {formattedMean}
    </TD>
  );
};

export const ReloadStreamStatsTD = ({ sectionId }: { sectionId: string }) => {
  // Record keyed by sectionId; consumers look up arbitrary sections.
  // eslint-disable-next-line @minervaproject/no-object-from-use-app-selector/require-properties-on-selector-hooks
  const stats = useObjectSelector(selectMonitoringStats);
  const { formattedMean, mean, percentAffectedUsers, nAffectedUsers } = getStatsBySectionIdAndType(
    stats,
    sectionId,
    MonitoringEventTypeType.RELOAD_STREAM,
    true,
  );
  return (
    <TD
      conditionForDanger={
        (mean && mean >= 1.25) ||
        (!!percentAffectedUsers && percentAffectedUsers >= 50 && nAffectedUsers > 3)
      }
      conditionForWarning={
        (mean && mean >= 0.5) || (!!percentAffectedUsers && percentAffectedUsers >= 40)
      }
      dataTestId="reload-stream-stats"
    >
      {formattedMean}
    </TD>
  );
};
